import { useEffect, useMemo } from 'react'
import { USER_COLORS } from '@/constants/monitor'
import { useLocationCircles } from '@/hooks/useActiveLocations'
import { useBatchUsers } from '@/hooks/useBatchUsers'
import { useMovementData } from '@/hooks/useMovementData'
import { useOrgUserIds } from '@/hooks/useOrgUserIds'
import { useUsersTrackingStatus } from '@/hooks/useUsersTrackingStatus'
import { getDayRange, pbDate } from '@/lib/dates'
import {
    useIsViewingToday,
    useMonitorDateTimeState,
    useMonitorMoment,
    useMonitorStore,
} from '@/stores/monitor-store'
import type { BulkMovement, PathPoint, UserLocation } from '@/types/movement'
import type { Users } from '@/types/pbSchema'
import { useNowTick } from './useNowTick'
import { type DayEntrySummary, useTodayEntriesByUser } from './useTodayEntriesByUser'

function buildAutoColorPicker(usedColors: Set<string>) {
    const available = USER_COLORS.filter(c => !usedColors.has(c))
    let index = 0
    return () => {
        const palette = available.length > 0 ? available : USER_COLORS
        const color = palette[index % palette.length]
        index++
        return color
    }
}

// When a snapshot moment is selected, drop movement points after it so the
// "most recent location" reflects where each user was at T (not now). Users
// with no points <= T fall through to an empty path and no entry in the map;
// the caller seeds the resulting maps with `undefined`/`[]` for those ids,
// which downstream classifies as "no data" — the desired behavior.
function groupMovementsByUser(
    movements: BulkMovement[],
    fetchedUsers: Map<string, Users>,
    userColorMap: Map<string, string>,
    cutoff: Date | null
): UserLocation[] {
    const userMap = new Map<string, UserLocation>()
    const assignedColors = new Set(userColorMap.values())
    const nextAutoColor = buildAutoColorPicker(assignedColors)
    const cutoffMs = cutoff ? cutoff.getTime() : null

    for (const movement of movements) {
        if (!movement.user) continue
        if (cutoffMs !== null && new Date(movement.timestamp).getTime() > cutoffMs) continue

        const userId = movement.user
        let userLocation = userMap.get(userId)

        if (!userLocation) {
            const storedColor = userColorMap.get(userId)
            userLocation = {
                userId,
                userName: fetchedUsers.get(userId)?.name ?? 'Unknown User',
                path: [],
                color: storedColor || nextAutoColor(),
                visible: true,
            }
            userMap.set(userId, userLocation)
        }

        userLocation.path.push(movement)

        if (
            !userLocation.mostRecentLocation ||
            new Date(movement.timestamp) > new Date(userLocation.mostRecentLocation.timestamp)
        ) {
            userLocation.mostRecentLocation = movement
        }
    }

    return Array.from(userMap.values())
}

export interface MonitorBaseResult {
    orgSlug: string | undefined
    orgId: string | undefined
    orgUserIds: string[]
    userColorMap: Map<string, string>
    fetchedUsers: Map<string, Users>
    rawUserLocations: UserLocation[]
    mostRecentLocationMap: Map<string, PathPoint | undefined>
    pathMap: Map<string, PathPoint[]>
    hasAnyMovementMap: Map<string, boolean>
    dayEntries: Map<string, DayEntrySummary>
    locationCircles: ReturnType<typeof useLocationCircles>['circles']
    isPending: boolean
    isLoadingMovements: boolean
    now: Date
}

export function useMonitorBase(): MonitorBaseResult {
    const { dateRange, setDateRange } = useMonitorDateTimeState()
    const { selectedMoment } = useMonitorMoment()
    const initializeVisibility = useMonitorStore(state => state.initializeVisibility)

    const isViewingToday = useIsViewingToday()
    const tick = useNowTick(60_000)
    const effectiveNow = selectedMoment ?? tick

    // If the page stays open across midnight, advance the stored range to
    // the new calendar day so realtime resubscribes and bulk-load re-keys.
    // Without this the monitor gets frozen on yesterday's window and every
    // user appears idle for 12+ hours until the app is restarted.
    // Skipped while a snapshot moment is selected — the user is intentionally
    // viewing a frozen point and shouldn't be yanked back to today.
    useEffect(() => {
        if (selectedMoment) return
        if (dateRange.startDate.toDateString() === tick.toDateString()) return
        const { startDate, endDate } = getDayRange(tick)
        setDateRange({ startDate, endDate })
    }, [tick, dateRange.startDate, setDateRange, selectedMoment])

    const { orgSlug, orgId, orgUserIds, userColorMap, isPending: isLoadingOrg } = useOrgUserIds()

    // When a snapshot is selected, query its calendar day; otherwise the stored
    // dateRange (which midnight rollover keeps current) drives the window.
    const queryDayBase = selectedMoment ?? dateRange.startDate
    const fullDayRange = useMemo(() => getDayRange(queryDayBase), [queryDayBase])
    const startDateStr = pbDate(fullDayRange.startDate)
    const endDateStr = pbDate(fullDayRange.endDate)

    const { movements, isPending: isLoadingMovements } = useMovementData({
        orgSlug,
        userIds: orgUserIds,
        startDate: startDateStr,
        endDate: endDateStr,
        realtime: !selectedMoment && isViewingToday,
    })

    const { userMap: fetchedUsers, isPending: isLoadingUsers } = useBatchUsers(orgUserIds)

    const rawUserLocations = useMemo(
        () => groupMovementsByUser(movements, fetchedUsers, userColorMap, selectedMoment),
        [movements, fetchedUsers, userColorMap, selectedMoment]
    )

    const mostRecentLocationMap = useMemo(() => {
        const map = new Map<string, PathPoint | undefined>()
        for (const id of orgUserIds) map.set(id, undefined)
        for (const u of rawUserLocations) map.set(u.userId, u.mostRecentLocation)
        return map
    }, [rawUserLocations, orgUserIds])

    const pathMap = useMemo(() => {
        const map = new Map<string, PathPoint[]>()
        for (const id of orgUserIds) map.set(id, [])
        for (const u of rawUserLocations) map.set(u.userId, u.path)
        return map
    }, [rawUserLocations, orgUserIds])

    const hasAnyMovementMap = useMemo(() => {
        const map = new Map<string, boolean>()
        for (const id of orgUserIds) map.set(id, false)
        for (const u of rawUserLocations) map.set(u.userId, u.path.length > 0)
        return map
    }, [rawUserLocations, orgUserIds])

    const { circles: locationCircles, isPending: isLoadingLocations } = useLocationCircles(orgId)
    const { dayEntries, isPending: isLoadingDayEntries } = useTodayEntriesByUser(
        orgUserIds,
        effectiveNow,
        selectedMoment ?? undefined
    )

    useEffect(() => {
        if (orgUserIds.length > 0) {
            initializeVisibility(orgUserIds)
        }
    }, [orgUserIds, initializeVisibility])

    const isPending =
        isLoadingOrg ||
        isLoadingMovements ||
        isLoadingUsers ||
        isLoadingLocations ||
        isLoadingDayEntries

    return {
        orgSlug,
        orgId,
        orgUserIds,
        userColorMap,
        fetchedUsers,
        rawUserLocations,
        mostRecentLocationMap,
        pathMap,
        hasAnyMovementMap,
        dayEntries,
        locationCircles,
        isPending,
        isLoadingMovements,
        now: effectiveNow,
    }
}

export function useMonitorStatuses(base: MonitorBaseResult) {
    // The classifier needs to know which job each user is currently clocked
    // into so on-site detection can be restricted to that job's geofences.
    // Presence of an id also doubles as the "clocked in" flag for the
    // late/early branches.
    const activeJobIdMap = useMemo(() => {
        const map = new Map<string, string | undefined>()
        for (const id of base.orgUserIds) {
            map.set(id, base.dayEntries.get(id)?.currentEntry?.jobId ?? undefined)
        }
        return map
    }, [base.orgUserIds, base.dayEntries])

    return useUsersTrackingStatus({
        userMap: base.fetchedUsers,
        orgId: base.orgId,
        mostRecentLocationMap: base.mostRecentLocationMap,
        pathMap: base.pathMap,
        hasAnyMovementMap: base.hasAnyMovementMap,
        activeJobIdMap,
        geofences: base.locationCircles,
        now: base.now,
    })
}
